import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  HelpCircle,
  Search,
  X,
  Calendar,
  FileText,
  Play,
  Trash2,
} from 'lucide-react'
import { FormEvent, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import paymentService from '../utils/paymentService'
import selfAssessmentApiService from '../utils/selfAssessmentApiService'
import saOnlineService from '../utils/saOnlineService'
import type { PricingPlan } from '../types'

// A draft row as returned by GET /sa-online/returns?status=draft (backend SAReturn).
interface SaDraftRow {
  _id: string
  taxYear: string
  uniqueTaxpayerReference?: string
  status?: string
  updatedAt?: string
  createdAt?: string
  personalDetails?: { nationalInsuranceNumber?: string }
}

type SelfAssessmentScope = 'self-employment' | 'uk-property' | 'foreign-property'

interface SelfAssessmentReturn {
  utr: string
  taxYear: string
      dueDate: string
  daysLeft: number
  status: 'pending' | 'submitted'
}

// Earliest tax year selectable for filing (including amended returns for prior
// periods). 2020 => "2020-21".
const EARLIEST_FILING_YEAR_START = 2020

// Start calendar year of the current (ongoing) UK tax year.
// UK tax year runs from April 6 to April 5 of the next year, so before April 6
// we're still in the previous calendar year's tax year.
const getCurrentTaxYearStart = (): number => {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() // 0-indexed (0 = January)
  const currentDay = now.getDate()

  if (currentMonth < 3 || (currentMonth === 3 && currentDay < 6)) {
    return currentYear - 1
  }
  return currentYear
}

const formatTaxYear = (startYear: number): string =>
  `${startYear}-${(startYear + 1).toString().slice(-2)}`

// The most recently *completed* tax year — the one a Self Assessment return is
// currently due for (e.g. in 2026 this is "2025-26", due by 31 January 2027).
// This is the sensible default selection.
const getCurrentFilingYear = (): string => formatTaxYear(getCurrentTaxYearStart() - 1)

// Generate selectable tax years, newest first: from the current filing year back
// to EARLIEST_FILING_YEAR_START so users can also file amended returns for
// previous periods. Future (not-yet-completed) tax years are excluded since a
// Self Assessment return can't be filed for a year that hasn't ended.
const generateTaxYears = (): string[] => {
  const latestStart = getCurrentTaxYearStart() - 1
  const years: string[] = []
  for (let year = latestStart; year >= EARLIEST_FILING_YEAR_START; year--) {
    years.push(formatTaxYear(year))
  }
  return years
}

export default function AddSelfAssessmentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const flowParam = new URLSearchParams(location.search).get('flow')
  const [scope, setScope] = useState<SelfAssessmentScope>('self-employment')
  const [searchValue, setSearchValue] = useState('')
  const [taxYear, setTaxYear] = useState<string>(() => getCurrentFilingYear()) // Default to the year currently due (e.g. 2025-26)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string>('')
  const [plans, setPlans] = useState<PricingPlan[]>([])
  const [isLoadingPlans, setIsLoadingPlans] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selfAssessmentReturn, setSelfAssessmentReturn] = useState<SelfAssessmentReturn | null>(null)
  const [localDrafts, setLocalDrafts] = useState<SaDraftRow[]>([])
  const [manualUtr, setManualUtr] = useState('')

  const loadDrafts = async () => {
    try {
      const drafts = await saOnlineService.listDraftReturns()
      setLocalDrafts(Array.isArray(drafts) ? (drafts as SaDraftRow[]) : [])
    } catch (err) {
      console.error('Failed to load draft returns:', err)
      setLocalDrafts([])
    }
  }

  // Load pricing plans and draft returns (from the backend) on mount
  useEffect(() => {
    loadPlans()
    loadDrafts()
  }, [])

  const loadPlans = async () => {
    try {
      setIsLoadingPlans(true)
      const response = await paymentService.listPricingPlans()
      const saPlans: PricingPlan[] = (response as any)?.['self-assessment'] || []
      const sortedPlans = saPlans.sort((a: PricingPlan, b: PricingPlan) => {
        if (a.sortOrder && b.sortOrder) {
          return a.sortOrder - b.sortOrder
        }
        return a.price - b.price
      })
      setPlans(sortedPlans)
      // Pre-select first plan if available
      if (sortedPlans.length > 0 && sortedPlans[0]) {
        setSelectedPlanSlug(sortedPlans[0].slug)
      }
    } catch (error) {
      console.error('Failed to fetch Self Assessment plans:', error)
      setError('Unable to load pricing plans. Please try again.')
    } finally {
      setIsLoadingPlans(false)
    }
  }

  const handleProceed = async () => {
    if (!searchValue.trim()) {
      setError('Please enter a valid National Insurance Number')
      return
    }

    // Validate NINO format (basic validation: 2 letters, 6 digits, 1 letter)
    const ninoPattern = /^[A-Z]{2}[0-9]{6}[A-Z]{1}$/i
    const normalizedNino = searchValue.replace(/\s/g, '').toUpperCase()
    
    if (!ninoPattern.test(normalizedNino)) {
      setError('Please enter a valid National Insurance Number (e.g. AB123456C)')
        return
      }

    setIsLoading(true)
    setError(null)

    try {
      // Calculate due date: 31 January of the year after the tax year ends.
      // e.g. tax year 2025-26 (ends April 2026) is due by 31 January 2027.
      const taxYearParts = taxYear.split('-')
      const endYear = parseInt('20' + taxYearParts[1])
      const dueDate = new Date(endYear + 1, 0, 31) // 31 January following tax year end
      const today = new Date()
      const daysLeft = Math.max(0, Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
      
      // Format due date
      const dueDateFormatted = dueDate.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })

      let utr = ''
      
      // Only try to get business details for Traditional MTD flow (requires HMRC auth)
      // SA Online flow doesn't need business details - user can enter UTR manually
      if (flowParam !== 'sa') {
        try {
          // Get business details which may contain UTR and other info
          // This call won't logout even if HMRC auth fails (skipAuthLogout: true)
          const businesses = await selfAssessmentApiService.getBusinessDetails(normalizedNino)
          
          // Try to extract UTR from business details
          if (businesses && businesses.length > 0 && (businesses[0] as any).utr) {
            utr = (businesses[0] as any).utr
          }
        } catch (businessErr: any) {
          // For Traditional MTD, if business details fail, it's okay - user can enter UTR manually
          console.log('Could not fetch business details (non-critical):', businessErr)
          // Don't set error here - allow user to proceed and enter UTR manually
        }
      }

      setSelfAssessmentReturn({
        utr,
        taxYear: taxYear,
        dueDate: dueDateFormatted,
        daysLeft: daysLeft,
        status: 'pending'
      })
      setManualUtr('')
      // if (!utr) {
      //   setError('Unable to retrieve a valid 10-digit UTR for this NINO. Please enter it below to continue.')
      // }
    } catch (err: any) {
      console.error('Error fetching self-assessment details:', err)
      setError(err.message || 'Failed to retrieve self-assessment details. Please check your NINO and try again.')
      setSelfAssessmentReturn(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // Don't auto-submit, require explicit "Proceed" button click
  }

  const handleClearSearch = () => {
    setSearchValue('')
    setSelfAssessmentReturn(null)
    setError(null)
  }

  const handleCompleteReturn = () => {
    // Only require a plan when plans are actually available. If none loaded
    // (empty/failed), don't dead-end the user — the filing wizard loads its own
    // plans and gates payment at submission.
    if (plans.length > 0 && !selectedPlanSlug) {
      setError('Please select a plan before proceeding.')
      return
    }

    if (!selfAssessmentReturn || !searchValue) {
      setError('Please search for your National Insurance Number first.')
      return
    }

    const normalizedUtr = (manualUtr || selfAssessmentReturn.utr).replace(/\s/g, '')
    if (!/^\d{10}$/.test(normalizedUtr)) {
      setError('Please enter a valid 10-digit UTR to continue.')
      return
    }

    // Determine flow type: 'sa' for SA Online, 'traditional' for Traditional MTD
    // If flowParam is 'sa', use 'sa', otherwise default to 'traditional'
    const flowType = flowParam === 'sa' ? 'sa' : 'traditional'

    const params = new URLSearchParams({
      utr: normalizedUtr,
      nino: searchValue.replace(/\s/g, '').toUpperCase(),
      taxYear: taxYear,
      planSlug: selectedPlanSlug,
      flow: flowType // Always set the flow parameter explicitly
    })
    
    navigate(`/self-assessment/filing?${params.toString()}`)
  }

  const resumeDraft = (draft: SaDraftRow) => {
    // Resume by loading the return from the backend (saReturnId in the URL).
    const params = new URLSearchParams({
      saReturnId: draft._id,
      nino: draft.personalDetails?.nationalInsuranceNumber || '',
      utr: draft.uniqueTaxpayerReference || '',
      taxYear: draft.taxYear,
      flow: 'sa',
    })
    navigate(`/self-assessment/filing?${params.toString()}`)
  }

  const deleteDraft = async (draft: SaDraftRow) => {
    try {
      await saOnlineService.deleteReturn(draft._id)
    } catch (err) {
      console.error('Failed to delete draft return:', err)
    }
    loadDrafts()
  }

  return (
    <AuthenticatedLayout activeTab='dashboard'>
      <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
        <div className='mx-auto'>
          {/* Header with Back Button and Title */}
          <div className='mb-8'>
            <div className='mb-4 flex items-center justify-between'>
              <button
                onClick={() => navigate(-1)}
                className='inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition'
              >
                <div className='flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm hover:shadow-md transition'>
                  <ArrowLeft className='h-5 w-5' />
                </div>
              </button>

              <div className='flex items-center justify-center gap-2'>
                <h1 className='text-2xl font-bold text-gray-900'>Add Self Assessment</h1>
                <button className='flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-400 text-gray-400 hover:border-gray-600 hover:text-gray-600 transition'>
                  <HelpCircle className='h-4 w-4' />
                </button>
              </div>
            </div>
            <p className='mx-auto max-w-3xl text-sm text-gray-500 leading-relaxed text-center'>
              {flowParam === 'sa' ? (
                <>
                  <strong className='text-blue-600'>Self Assessment:</strong> File your self-assessment tax return using the traditional HMRC process.
                  This is ideal for straightforward returns. Each assessment is unique per NINO and tax year combination.
                </>
              ) : (
                <>
                  <strong className='text-green-600'>Making Tax Digital (MTD):</strong> File your self-assessment using HMRC&apos;s Making Tax Digital APIs.
                  This flow supports all income sources and advanced features. Each assessment is unique per NINO and tax year combination.
                </>
              )}
            </p>
            
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className='mb-8'>
            <div className='flex flex-col gap-4 md:flex-row md:items-center'>
              {/* Tax Year Dropdown */}
              <div className='relative w-full md:w-48'>
                <select
                  value={taxYear}
                  onChange={(e) => setTaxYear(e.target.value)}
                  className='w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 py-3.5 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
                >
                  {generateTaxYears().map((year) => (
                    <option key={year} value={year}>
                      {year.replace('-', ' to ')}
                    </option>
                  ))}
                </select>
                <ChevronDown className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
                  </div>

              {/* Scope Dropdown */}
              <div className='relative w-full md:w-64'>
              <select
                value={scope}
                  onChange={(e) => setScope(e.target.value as SelfAssessmentScope)}
                  className='w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 py-3.5 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
              >
                <option value='self-employment'>Self Employment</option>
                <option value='uk-property'>UK Property</option>
                <option value='foreign-property'>Foreign Property</option>
              </select>
                <ChevronDown className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
                      </div>

              {/* Search Input */}
              <div className='relative flex-1'>
                <div className='relative flex items-center'>
                  <Search className='absolute left-4 h-5 w-5 text-gray-400' />
                              <input
                                type='text'
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleProceed()
                      }
                    }}
                    placeholder='BZ809099A'
                    className={`w-full rounded-xl border bg-white py-3.5 pl-12 ${
                      searchValue ? 'pr-32' : 'pr-12'
                    } text-sm font-medium text-gray-900 shadow-sm placeholder:text-gray-400 hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition`}
                  />
                  {searchValue && (
                    <>
                  <button
                    type='button'
                        onClick={handleProceed}
                        disabled={isLoading}
                        className='absolute right-20 flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 transition'
                      >
                        {isLoading ? (
                          <div className='h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent' />
                            ) : (
                              <>
                            <span>Proceed</span>
                            <ArrowRight className='h-3 w-3' />
                              </>
                            )}
                      </button>
                        <button
                          type='button'
                        onClick={handleClearSearch}
                        className='absolute right-4 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition'
                      >
                        <X className='h-4 w-4' />
                      </button>
                </>
              )}
                          </div>
                        </div>
                      </div>
          </form>

          {/* Draft Assessments Table - Show only when user hasn't started typing a search */}
          {!selfAssessmentReturn && !searchValue.trim() && localDrafts.length > 0 && (
            <div className='mb-8'>
              <div className='mb-4 flex items-center justify-between'>
                <h2 className='text-lg font-semibold text-gray-900'>Draft Assessments</h2>
              </div>
              <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
                <table className='w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                        Tax Year
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                        NINO
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                        Flow
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                        Last Saved
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600'>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200 bg-white'>
                    {localDrafts.map((draft) => (
                      <tr key={draft._id} className='hover:bg-gray-50 transition'>
                        <td className='whitespace-nowrap px-6 py-4'>
                          <div className='flex items-center gap-2'>
                            <Calendar className='h-4 w-4 text-gray-400' />
                            <span className='text-sm font-medium text-gray-900'>
                              {draft.taxYear}
                            </span>
                          </div>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-left'>
                          <span className='text-sm text-gray-700'>{draft.personalDetails?.nationalInsuranceNumber || '—'}</span>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-left'>
                          <span className='inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700'>
                            Self Assessment
                          </span>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4'>
                          <div className='flex items-center gap-2'>
                            <FileText className='h-4 w-4 text-gray-400' />
                            <span className='text-sm text-gray-700'>
                              {new Date(draft.updatedAt || draft.createdAt || Date.now()).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-right'>
                          <div className='flex items-center justify-end gap-2'>
                            <button
                              onClick={() => resumeDraft(draft)}
                              className='inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 transition'
                            >
                              <Play className='h-3 w-3' />
                              Resume
                            </button>
                            <button
                              onClick={() => deleteDraft(draft)}
                              className='inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition'
                            >
                              <Trash2 className='h-3 w-3' />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className='mb-6 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
              <AlertCircle className='h-5 w-5 flex-shrink-0' />
              <span>{error}</span>
                        </div>
                  )}

          {/* Self Assessment Return Card */}
          {selfAssessmentReturn && (
            <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
              <div className='mb-6 border-b border-gray-200 pb-4'>
                <h2 className='text-lg font-semibold text-gray-900'>Self Assessment</h2>
                {selfAssessmentReturn.utr && selfAssessmentReturn.utr !== 'Pending retrieval' ? (
                  <p className='mt-1 text-sm text-gray-600'>
                    Self Assessment Unique Taxpayer Reference (UTR):{' '}
                    <span className='font-semibold text-gray-900'>{selfAssessmentReturn.utr}</span>
                  </p>
                ) : (
                  <p className='mt-1 text-sm text-gray-600'>
                    National Insurance Number (NINO):{' '}
                    <span className='font-semibold text-gray-900'>{searchValue}</span>
                  </p>
                )}
                      </div>

                <div className='space-y-6'>
                  {!selfAssessmentReturn.utr && (
                    <div>
                      <h3 className='mb-3 text-base font-semibold text-gray-900'>Enter your UTR</h3>
                      <div className='rounded-xl border border-gray-200 bg-gray-50 p-5'>
                        <label className='mb-2 block text-sm font-medium text-gray-700'>
                          Self Assessment Unique Taxpayer Reference (10 digits)
                        </label>
                        <input
                          type='text'
                          inputMode='numeric'
                          value={manualUtr}
                          onChange={(e) => setManualUtr(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          className='w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200'
                          placeholder='1234567890'
                        />
                        <p className='mt-2 text-xs text-gray-500'>
                          You can find this on HMRC letters or your personal tax account.
                        </p>
                      </div>
                    </div>
                  )}
                      <div>
                    <h3 className='mb-3 text-base font-semibold text-gray-900'>Your Returns</h3>
                    
                    <div className='rounded-xl border border-gray-200 bg-gray-50 p-5'>
                      <h4 className='mb-2 text-sm font-semibold text-gray-900'>Deadline Details</h4>
                      <p className='text-sm text-gray-700'>
                        {taxYear.replace('-', ' to ')} tax return due by{' '}
                        <span className='font-semibold'>{selfAssessmentReturn.dueDate}</span>{' '}
                        <span className='text-red-600 font-semibold'>
                          ({selfAssessmentReturn.daysLeft} days left)
                        </span>
                      </p>
                        </div>
                      </div>

                  {/* Plan Selection */}
                  {plans.length > 0 && (
                        <div>
                      <h3 className='mb-3 text-base font-semibold text-gray-900'>Select Plan</h3>
                      <div className='relative rounded-xl border border-gray-200 bg-white p-5'>
                          <select
                          value={selectedPlanSlug}
                          onChange={(e) => setSelectedPlanSlug(e.target.value)}
                          disabled={isLoadingPlans}
                          className='w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition disabled:opacity-50'
                        >
                          {plans.map((plan) => (
                            <option key={plan.slug} value={plan.slug}>
                              {plan.name} - £{plan.price.toFixed(2)}
                              </option>
                            ))}
                          </select>
                        <ChevronDown className='pointer-events-none absolute right-7 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
                        </div>
                    </div>
                  )}

                  <button
                    onClick={handleCompleteReturn}
                    disabled={isLoading || isLoadingPlans || (plans.length > 0 && !selectedPlanSlug)}
                    className='flex w-full items-center justify-between rounded-xl bg-gray-900 px-6 py-4 text-left text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 transition'
                  >
                    <span>Complete Self Assessment return {taxYear.replace('-', ' to ')}</span>
                    <ArrowRight className='h-5 w-5 flex-shrink-0' />
                  </button>
          </div>
        </div>
      )}
              </div>
            </div>

    </AuthenticatedLayout>
  )
}
